// src/QrCodeModal.js
// Окно с QR-кодом ссылки: навести камеру телефона и открыть адрес,
// не набирая его вручную. Код строится прямо в браузере — адрес никуда
// не отправляется.

import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import qrcode from 'qrcode-generator';
import './QrCodeModal.css';

// Кириллица в адресе (кабинет.рф, пути на русском) иначе закодировалась бы
// неверно: по умолчанию библиотека берёт только младший байт символа
qrcode.stringToBytes = qrcode.stringToBytesFuncs['UTF-8'];

// Поле тишины вокруг кода: без него часть камер код не распознаёт
const QUIET_ZONE = 4;

const buildQr = (text) => {
  try {
    // 0 — размер подбирается по длине адреса; M — запас на ~15% повреждений
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const count = qr.getModuleCount();
    let path = '';
    for (let row = 0; row < count; row += 1) {
      for (let col = 0; col < count; col += 1) {
        if (qr.isDark(row, col)) {
          path += `M${col + QUIET_ZONE},${row + QUIET_ZONE}h1v1h-1z`;
        }
      }
    }
    return { size: count + QUIET_ZONE * 2, path };
  } catch (err) {
    return null;
  }
};

export default function QrCodeModal({ title, url, onClose }) {
  const qr = useMemo(() => buildQr(url), [url]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    // Здесь терять нечего, поэтому окно закрывается и кликом мимо него
    <div className="qr-modal" onClick={onClose}>
      <div
        className="qr-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="qr-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="qr-modal-header">
          <h3 id="qr-modal-title" className="qr-modal-title">{title}</h3>
          <button type="button" className="qr-modal-close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        {qr ? (
          <div className="qr-modal-code">
            <svg
              viewBox={`0 0 ${qr.size} ${qr.size}`}
              shapeRendering="crispEdges"
              role="img"
              aria-label={`QR-код ссылки ${url}`}
            >
              <rect width={qr.size} height={qr.size} fill="#ffffff" />
              <path d={qr.path} fill="#000000" />
            </svg>
          </div>
        ) : (
          <p className="qr-modal-error">Не удалось построить QR-код для этого адреса.</p>
        )}

        <p className="qr-modal-hint">Наведите камеру телефона на код, чтобы открыть ссылку</p>
        <a className="qr-modal-url" href={url} target="_blank" rel="noopener noreferrer">{url}</a>
      </div>
    </div>,
    document.body
  );
}
